import { useEffect, useState } from 'react';

/* РАЗДЕЛЫ КАБИНЕТА — АДРЕСАМИ, а не состоянием.

   В макете переключение сделано на `display:none` и одном `goTab()`: для
   прототипа этого хватает, для кабинета — нет. Причины ровно три, и все
   встречаются в первый же день:

     * пациент присылает ссылку «мои планы» или открывает раздел в новой вкладке;
     * «назад» в браузере обязан возвращать в предыдущий раздел, а не уводить с
       сайта целиком;
     * Keycloak возвращает на КОНКРЕТНЫЙ адрес (`/login`), и он должен быть
       маршрутом, а не разделом внутри состояния.

   `pushState`, а не `hash`: адрес должен читаться как адрес (`/care`), а сервер
   отдаёт index.html на любой путь — см. nginx.conf. */

export type Tab = 'visits' | 'care' | 'doctors' | 'profile';

export type Route =
    | { kind: 'tab'; tab: Tab }
    /** Один план ухода: `/plan/<id>`.
     *
     *  ПОЧЕМУ НЕ `/care/<id>`, хотя раздел зовётся `care`. Адрес `/care/<токен>`
     *  зарезервирован под ПЕРЕДАЧУ плана по ссылке: врач присылает пациенту
     *  строку `<care_link_root><секрет>`, и корень — это `…/care/`. Положив сюда
     *  же id плана, мы получили бы два разных смысла у одного шаблона пути, и
     *  различать их пришлось бы угадыванием формы строки. */
    | { kind: 'plan'; id: string }
    /** Приём ссылки на план: `/care/<секрет>`. Именно этот адрес врач присылает
     *  пациенту — корень строки задан в `POST_CARE__CARE_LINK_ROOT` у сервиса, и
     *  менять его форму здесь нельзя: она уже напечатана в чужих сообщениях. */
    | { kind: 'claim'; token: string }
    /** Возврат от Keycloak: `/login?code=…`. Раздел кабинета, а не его страница. */
    | { kind: 'login' }
    /** Возврат от Keycloak после «удалить аккаунт»: `/account-deleted?ticket=…`
     *  (lib/account.ts). Как и `/login` — адрес, на который Keycloak обязан вернуть. */
    | { kind: 'deleted' };

const PATHS: Record<Tab, string> = {
    visits: '/',
    care: '/care',
    doctors: '/doctors',
    profile: '/profile',
};

export function tabPath(tab: Tab): string {
    return PATHS[tab];
}

export function planPath(id: string): string {
    return `/plan/${encodeURIComponent(id)}`;
}

function fromLocation(): Route {
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    if (path === '/login') return { kind: 'login' };
    if (path === '/account-deleted') return { kind: 'deleted' };
    const plan = /^\/plan\/([^/]+)$/.exec(path);
    if (plan) return { kind: 'plan', id: decodeURIComponent(plan[1]) };
    if (path === '/care') return { kind: 'tab', tab: 'care' };
    // ПОСЛЕ точного `/care`: раздел и приём ссылки различаются наличием сегмента.
    const claim = /^\/care\/([^/]+)$/.exec(path);
    if (claim) return { kind: 'claim', token: decodeURIComponent(claim[1]) };
    if (path === '/doctors') return { kind: 'tab', tab: 'doctors' };
    if (path === '/profile') return { kind: 'tab', tab: 'profile' };
    // Неизвестный путь — визиты, а не «страница не найдена»: кабинет закрыт
    // логином, чужих ссылок в нём не бывает, а опечатка в адресе не повод
    // показывать ошибку вместо оболочки.
    return { kind: 'tab', tab: 'visits' };
}

/** Текущий раздел плюс переход. Слушает «назад»/«вперёд» браузера. */
export function useRoute(): {
    route: Route;
    go: (tab: Tab) => void;
    openPlan: (id: string) => void;
} {
    const [route, setRoute] = useState<Route>(fromLocation);

    useEffect(() => {
        const onPop = () => setRoute(fromLocation());
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, []);

    function go(tab: Tab) {
        const path = PATHS[tab];
        if (window.location.pathname !== path) window.history.pushState(null, '', path);
        setRoute({ kind: 'tab', tab });
        // Наверх — как в макете: разделы разной длины, и остаться на середине
        // прокрутки после перехода значит увидеть чужую середину.
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    /** Переход на страницу одного плана. Отдельно от `go`, потому что раздел и
     *  страница внутри раздела — разные вещи: в меню подсвечивается «планы
     *  ухода», а адрес ведёт на конкретный план и делится ссылкой как есть. */
    function openPlan(id: string) {
        const path = planPath(id);
        if (window.location.pathname !== path) window.history.pushState(null, '', path);
        setRoute({ kind: 'plan', id });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    return { route, go, openPlan };
}
